import {useMediaQueries, useQueryParams} from 'presentation/pages';
import {HandleSelectCardParams} from '../../types';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {UseOdontoUnitsSelfNetworkProps} from './types';
import {
	MAX_DISTANCE_FILTER_UNITS_ODONTO,
	MIN_DISTANCE_IN_KM,
} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useOpenDrawerFilterStore} from 'presentation/modules/booking/shared/store/useOpenDrawerFilterStore';
import {translations} from 'presentation/translations-new';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

const {changeDistance, changeFilter, searchNearbyUnitsHealthExam, selfNetwork} =
	translations['pt-br'].privateFlow.bookingFlow.unitsFlow;

export function useOdontoUnitsSelfNetwork({
	handleNextStep,
	unitsList,
}: UseOdontoUnitsSelfNetworkProps) {
	const {specialtyName, isManualLocation, hasGeoDistrictsFilter, districtName} = useBookingStore(
		state => state.bookingData,
	);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const setOpenDrawerState = useOpenDrawerFilterStore(state => state.setOpenDrawerState);
	const {isMobile} = useMediaQueries();
	const {
		SEARCH_PARAMS: {query, localityDistance},
		clearSearch,
	} = useQueryParams();
	const {isEnabled: isUnitsNetworksEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_UNITS_NETWORKS,
	);

	const shouldShowDistanceReference = !isManualLocation && !hasGeoDistrictsFilter;

	const isDistanceFilterActive =
		Boolean(localityDistance) && localityDistance !== MIN_DISTANCE_IN_KM;
	const districtGeolocationFilterIsActive = !isManualLocation && districtName;

	const filterIsActive = isDistanceFilterActive || districtGeolocationFilterIsActive;

	const showEmptyAlert = unitsList?.units?.unitsByLocality?.length === 0;
	const isDistanceAtMax = Number(localityDistance) >= MAX_DISTANCE_FILTER_UNITS_ODONTO;

	const shouldHideDrawerButton =
		isManualLocation || (isUnitsNetworksEnabled && showEmptyAlert && isDistanceAtMax);

	const getEmptyAlertDescription = () => {
		if (isDistanceFilterActive && !(isUnitsNetworksEnabled && isDistanceAtMax)) {
			return `${selfNetwork.emptyDescriptionInKmPart1}${localityDistance}${selfNetwork.emptyDescriptionInKmPart2}`;
		}

		return selfNetwork.emptyDescription;
	};

	const handleSelectCard = (params: HandleSelectCardParams) => {
		setBookingData(prev => ({
			...prev,
			unitCode: params.code,
			unitName: params.name,
			unitAddress: params.address,
			bookingRedirect: false,
			odontoUnitDistrictSelected: params.district,
		}));

		clearSearch();
		handleNextStep();
	};

	const openDrawerButtonLabel = () => {
		if (!filterIsActive) return searchNearbyUnitsHealthExam;

		return districtGeolocationFilterIsActive ? changeFilter : changeDistance;
	};

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
	};

	return {
		handleSelectCard,
		query,
		isMobile,
		specialtyName,
		shouldShowDistanceReference,
		filterIsActive,
		handleOpenDrawer,
		openDrawerButtonLabel,
		shouldHideDrawerButton,
		showEmptyAlert,
		getEmptyAlertDescription,
	};
}
